import { find, compact, map } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import { ProfileTypes } from '@sda/enums';
import { isPrimitive } from '@sda/utils/type';
import { getNormalizedProfile } from '@sda/utils/meta';
import { getSelectedProfileId } from '../helpers/profile';
import { getUserId } from '../helpers/session';
import {
  selectItemById, defaultItemSelectorById, selectItemByOptionalId, selectItemByArray,
} from '../lib';
import {
  threadSelectors, currentThreadSelector, threadDeletionSelector, actSelectors,
  venueSelectors,
  showSelectors,
  profileSelectors,
} from '..';
import { getThreadIdFromPath } from '../helpers/router';
import type { NormalizedPublicProfile } from '@sda/interfaces/show';

/**
 * Get a thread by ID
 * @param  {any} state
 * @param  {string} id
 * @return {string|null}
 */
export const getThreadById = threadSelectors.selectById;

export const getCurrentThreadId = createSelector(
  selectItemByOptionalId,
  currentThreadSelector,
  getThreadIdFromPath,
  (providedId, currentShowId, matchShowId) => {
    if (providedId && isPrimitive(providedId)) {
      return providedId;
    }

    return currentShowId || matchShowId;
  },
);

/**
 * Fetches the current selected thread
 */
export const getCurrentThread = createSelector(
  threadSelectors.selectEntities,
  getCurrentThreadId,
  (items, id) => defaultItemSelectorById(items, id),
);

/**
 * Retrieves all profiles for the current show
 */
export const getCurrentThreadProfiles = createSelector(
  getCurrentThread,
  actSelectors.selectEntities,
  venueSelectors.selectEntities,
  profileSelectors.selectEntities,
  (thread, acts, venues, pendingProfiles) => {
    if (!thread) {
      return [];
    }

    const profiles = compact(map(thread.publicProfiles, (profile) => {
      if (profile.profileType === ProfileTypes.ACT) {
        return acts[profile.profileId] as any;
      }

      if (profile.profileType === ProfileTypes.VENUE) {
        return venues[profile.profileId] as any;
      }

      if (profile.profileType === ProfileTypes.PENDING) {
        return pendingProfiles[profile.profileId] as any;
      }

      return null;
    })) as NormalizedPublicProfile[];

    return profiles;
  },
);

export const getCurrentThreadProfilesLoaded = createSelector(
  getCurrentThread,
  getCurrentThreadProfiles,
  (thread, profiles) => thread.profileIds.length === profiles.length,
);

export const getCurrentThreadOtherProfiles = createSelector(
  getCurrentThreadProfiles,
  getSelectedProfileId,
  (profiles, selectedProfileId) => profiles
    .filter((profile) => profile.id !== selectedProfileId)
    .map((profile) => getNormalizedProfile(profile.profileType, profile)),
);

export const getCurrentThreadHasShowFab = createSelector(
  getCurrentThread,
  getUserId,
  (thread, userId) => thread?.hasConnection || userId !== thread?.owner,
);

export const getThreadShowId = createSelector(
  getCurrentThread,
  (thread) => thread && thread.showId,
);

/**
 * Find the profile belonging to the user that has performed an action in a thread
 */
export const getProfileByUserId = createSelector(
  getCurrentThreadProfiles,
  selectItemByArray,
  (profiles, [userId]) => {
    if (!profiles) {
      return null;
    }

    return find(profiles, (profile) => profile.collaborators && profile.collaborators.indexOf(userId) !== -1);
  },
);

export const getThreadIsRecentlyDeleted = createSelector(
  threadDeletionSelector,
  selectItemById,
  (threadDeletions, threadId) => threadDeletions.indexOf(threadId) !== -1,
);

/**
 * Retrieve all shows for the current thread that the current user has access to
 */
export const getCurrentThreadShows = createSelector(
  getCurrentThreadId,
  getSelectedProfileId,
  showSelectors.selectAll,
  (currentThreadId, selectedProfileId, shows) => shows.filter(
    (show) => show.masterThreadId === currentThreadId,
  ),
);
